import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { formatInTimeZone } from 'date-fns-tz';
import { endOfDay, isAfter, parse, startOfDay, subYears } from 'date-fns';
import MaskedInput from 'react-text-mask';
import isEmpty from 'lodash.isempty';
import cn from 'classnames';

import { Button } from '../../../components';
import { PHONE_MASK } from '../../../config/constants';
import { isMobilePhoneInvalid } from '../../../lib/util';

const ScheduleTAVModal = ({
	isOpen,
	onClose,
	event = {},
	timeZone,
	provider = {},
	onSubmit,
	onVerifyMobileNumber,
	phoneData,
}) => {
	const [birthDate, setBirthDate] = useState('');
	const [isGuardianRequired, setIsGuardianRequired] = useState(false);
	const [dependent_first, setDependentFirstName] = useState('');
	const [dependent_last, setDependentLastName] = useState('');
	const [name_first, setNameFirst] = useState('');
	const [name_last, setNameLast] = useState('');
	const [mobile_phone, setMobilePhone] = useState('');
	const [email_address, setEmailAddress] = useState('');
	const [isPhoneVerified, setPhoneVerified] = useState(false);

	useEffect(() => {
		setNameFirst('');
		setNameLast('');
		setEmailAddress('');
		setMobilePhone('');
		setBirthDate('');
		setIsGuardianRequired(false);
		setDependentFirstName('');
		setDependentLastName('');
		setPhoneVerified(false);
	}, [isOpen]);

	useEffect(() => {
		if (!isPhoneVerified) {
			if (isMobilePhoneInvalid(mobile_phone)) {
				setPhoneVerified(false);
			} else if (
				!isEmpty(mobile_phone) &&
				!isMobilePhoneInvalid(mobile_phone) &&
				phoneData
			) {
				setPhoneVerified(true);
			}
		}
	}, [mobile_phone, phoneData, isPhoneVerified]);

	// validation
	const isValid =
		name_first &&
		name_first.length > 0 &&
		name_last &&
		name_last.length > 0 &&
		birthDate &&
		birthDate.length === 10 &&
		((email_address && email_address.length > 4) ||
			(!isEmpty(mobile_phone) &&
				mobile_phone.length === PHONE_MASK.length &&
				!isMobilePhoneInvalid(mobile_phone))) &&
		((isGuardianRequired &&
			dependent_first &&
			dependent_first.length > 0 &&
			dependent_last &&
			dependent_last.length > 0) ||
			!isGuardianRequired);

	const verifyDOB = ({ target }) => {
		setBirthDate(target.value);
		const birthDt = startOfDay(parse(target.value, 'yyyy-MM-dd', new Date()));
		setIsGuardianRequired(isAfter(birthDt, subYears(endOfDay(new Date()), 18)));
	};

	const handleVerifyMobile = () => {
		setPhoneVerified(false);
		if (
			!isEmpty(mobile_phone) &&
			mobile_phone.length === PHONE_MASK.length &&
			!isMobilePhoneInvalid(mobile_phone)
		) {
			onVerifyMobileNumber(mobile_phone);
		}
	};

	// save/create
	const handleSubmit = () => {
		const submissionData = {
			birth_dt: parse(birthDate, 'yyyy-MM-dd', new Date()).toISOString(),
			name_first,
			name_last,
			email_address,
			visit_provider_id: provider.providerId,
			mobile_phone: isMobilePhoneInvalid(mobile_phone, true),
			dependent_first,
			dependent_last,
			appointmentId: event.id,
		};
		onSubmit(submissionData);
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={onClose} className="modal-dialog">
			<div className="modal-content">
				<div className="modal-header">
					<button
						type="button"
						data-dismiss="modal"
						aria-label="Close"
						className="close"
						onClick={onClose}
					>
						<span aria-hidden="true">×</span>
					</button>
					<h4 className="modal-title">Text-a-Visit™ with Appointment</h4>
				</div>
				<div className="modal-body form-horizontal">
					<p>
						Fill in all fields to generate a one-time use code you can give to
						your patient to ease signing up
					</p>
					<div className="form-group">
						<label className="col-md-6 control-label">Start Date/Time</label>
						<div className="col-md-6">
							<p className="form-control-static">
								{event?.scheduled_start_dttm &&
									formatInTimeZone(
										new Date(event.scheduled_start_dttm),
										timeZone,
										'PPPPp'
									)}
							</p>
						</div>
					</div>
					<div className="form-group">
						<label className="col-md-6 control-label">Duration</label>
						<div className="col-md-6">
							<p className="form-control-static">
								{event.appointment_duration} minutes
							</p>
						</div>
					</div>
					{/* <div className="form-group">
						<label className="col-md-6 control-label">Notes</label>
						<div className="col-md-6">
							<p className="form-control-static">{event.notes ? event.notes : '<None Entered>'}</p>
						</div>
					</div> */}
					<div className="form-group">
						<label className="col-md-6 control-label">
							Patient Date of Birth
						</label>
						<div className="col-md-6">
							<input
								type="date"
								className="form-control"
								value={birthDate}
								onChange={verifyDOB}
							/>
						</div>
					</div>
					{isGuardianRequired && (
						<React.Fragment>
							<div className="form-group">
								<label className="col-md-6 control-label">
									Dependent (Patient) First Name
								</label>
								<div className="col-md-6">
									<input
										type="text"
										className="form-control"
										value={dependent_first}
										onChange={({ target }) =>
											setDependentFirstName(target.value)
										}
									/>
								</div>
							</div>
							<div className="form-group">
								<label className="col-md-6 control-label">
									Dependent (Patient) Last Name
								</label>
								<div className="col-md-6">
									<input
										type="text"
										className="form-control"
										value={dependent_last}
										onChange={({ target }) =>
											setDependentLastName(target.value)
										}
									/>
								</div>
							</div>
						</React.Fragment>
					)}
					<div className="form-group">
						<label className="col-md-6 control-label">
							{isGuardianRequired ? 'Guardian' : 'Patient'} First Name
						</label>
						<div className="col-md-6">
							<input
								type="text"
								className="form-control"
								value={name_first}
								onChange={({ target }) => setNameFirst(target.value)}
							/>
						</div>
					</div>
					<div className="form-group">
						<label className="col-md-6 control-label">
							{isGuardianRequired ? 'Guardian' : 'Patient'} Last Name
						</label>
						<div className="col-md-6">
							<input
								type="text"
								className="form-control"
								value={name_last}
								onChange={({ target }) => setNameLast(target.value)}
							/>
						</div>
					</div>
					<div
						className={cn('form-group', {
							'has-error':
								isPhoneVerified && phoneData && !phoneData.isTAVCapable,
						})}
					>
						<label className="col-md-6 control-label">
							{isGuardianRequired ? 'Guardian' : 'Patient'} Mobile Phone
						</label>
						<div className="col-md-6">
							<MaskedInput
								className="form-control"
								mask={PHONE_MASK}
								value={mobile_phone}
								onChange={({ target }) => setMobilePhone(target.value)}
								onBlur={handleVerifyMobile}
							/>
							{isPhoneVerified && (
								<React.Fragment>
									{phoneData && phoneData.isTAVCapable && (
										<span className="help-block">
											Phone number is Text-a-Visit capable
										</span>
									)}
									{phoneData && !phoneData.isTAVCapable && !phoneData.err && (
										<span className="help-block">
											Phone number is not Text-a-Visit capable
										</span>
									)}
									{phoneData && phoneData.err && (
										<span className="help-block">
											Unable to verify phone number
										</span>
									)}
								</React.Fragment>
							)}
						</div>
					</div>
					<div className="form-group">
						<label className="col-md-6 control-label">
							{isGuardianRequired ? 'Guardian' : 'Patient'} Email Address
						</label>
						<div className="col-md-6">
							<input
								type="email"
								className="form-control"
								value={email_address}
								onChange={({ target }) => setEmailAddress(target.value)}
							/>
						</div>
					</div>
					<div className="form-group">
						<label className="col-md-6 control-label">With Provider</label>
						<div className="col-md-6">
							<p className="form-control-static">
								{provider.name_first} {provider.name_last},{' '}
								{provider.credentials}
							</p>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<Button title="Cancel" onClick={onClose} />
					<Button
						primary
						title="Schedule Text-a-Visit"
						disabled={!isValid}
						onClick={handleSubmit}
					/>
				</div>
			</div>
		</Modal>
	);
};

ScheduleTAVModal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	event: PropTypes.object,
	timeZone: PropTypes.string.isRequired,
	provider: PropTypes.object,
	onSubmit: PropTypes.func,
	onVerifyMobileNumber: PropTypes.func,
	phoneData: PropTypes.object,
};

export default ScheduleTAVModal;
